import React, { useState, useMemo } from "react";
import {
  Box, Grid, Typography, TextField, Card, List, ListItem, 
  ListItemButton, ListItemText, Divider, Tabs, Tab, Dialog, 
  AppBar, Toolbar, IconButton, Button, Slide, Stack, Autocomplete,
  FormControlLabel, Switch, Paper, InputAdornment, Chip
} from "@mui/material";
import { 
  Search, Close, Description, ChevronRight, 
  FileDownload, PictureAsPdf, TableChart, Tune 
} from "@mui/icons-material";
import { reportCategories } from "./ReportsCategories.config";

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const EasyReports = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);

  const filteredReports = useMemo(() => {
    return reportCategories[activeTab].reports.filter(r => 
      r.title.toLowerCase().includes(search.toLowerCase())
    );
  }, [activeTab, search]);

  return (
    <Box sx={{ height: "100vh", bgcolor: "#F8FAFC", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      
      {/* হেডার এবং সার্চ */}
      <Paper elevation={0} sx={{ p: 3, borderBottom: "1px solid #E2E8F0", borderRadius: 0 }}>
        <Typography variant="h5" fontWeight="900" sx={{ mb: 2, color: "#0F172A" }}>Reports Library</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ "& .MuiTab-root": { fontWeight: 700 } }}>
            {reportCategories.map((cat, i) => <Tab key={i} label={cat.label} />)}
          </Tabs>
          <TextField
            placeholder="Search reports..."
            size="small"
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ width: { xs: "100%", md: 350 }, bgcolor: "white" }}
          />
        </Stack>
      </Paper>

      {/* রিপোর্ট কার্ড গ্রিড */}
      <Box sx={{ flexGrow: 1, p: 3, overflowY: "auto" }}>
        <Grid container spacing={2}>
          {filteredReports.map((report) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={report.id}>
              <Card 
                variant="outlined" 
                sx={{ 
                  borderRadius: "12px", transition: "0.2s",
                  "&:hover": { borderColor: "#2563EB", transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }
                }}
              >
                <ListItemButton onClick={() => setSelectedReport(report)} sx={{ py: 2 }}>
                  <Description color="primary" sx={{ mr: 1.5 }} />
                  <ListItemText 
                    primary={<Typography variant="body2" fontWeight="700">{report.title}</Typography>}
                    secondary={<Typography variant="caption" noWrap display="block">{report.desc}</Typography>}
                  />
                  <ChevronRight fontSize="small" color="disabled" />
                </ListItemButton>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ফুল-পেজ ডায়ালগ ফিল্টার হ্যান্ডলার */}
      <Dialog 
        fullScreen 
        open={Boolean(selectedReport)} 
        onClose={() => setSelectedReport(null)} 
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative", bgcolor: "#1E293B" }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => setSelectedReport(null)}><Close /></IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">Configuration: {selectedReport?.title}</Typography>
            <Stack direction="row" spacing={1}>
              <Button color="inherit" startIcon={<PictureAsPdf />}>Export PDF</Button>
              <Button color="inherit" startIcon={<TableChart />}>Export Excel</Button>
            </Stack>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: { xs: 3, md: 8 }, maxWidth: 800, mx: "auto", width: "100%" }}>
          <Stack spacing={4}>
            <Box>
              <Typography variant="h4" fontWeight="900" gutterBottom>{selectedReport?.title}</Typography>
              <Typography color="textSecondary">Set your parameters below to generate the report.</Typography>
            </Box>

            <Divider />

            {/* ডাইনামিক ফিল্টার ইঞ্জিন  */}
            <Grid container spacing={3}>
              {selectedReport?.filters.includes("dateRange") && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="700" gutterBottom>Select Timeframe *</Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField fullWidth type="date" label="Start Date" InputLabelProps={{ shrink: true }} />
                    <TextField fullWidth type="date" label="End Date" InputLabelProps={{ shrink: true }} />
                  </Stack>
                </Grid>
              )}

              {selectedReport?.filters.includes("user") && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="700" gutterBottom>Service User Selection</Typography>
                  <Autocomplete options={["All Users", "Client A", "Client B"]} renderInput={(params) => <TextField {...params} placeholder="Search Client..." />} />
                </Grid>
              )}

              {selectedReport?.filters.includes("employee") && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="700" gutterBottom>Select Staff Member</Typography>
                  <Autocomplete options={["All Staff", "John Doe", "Jane Smith"]} renderInput={(params) => <TextField {...params} placeholder="Search Staff..." />} />
                </Grid>
              )}

              {/* Detailed Care Report-এর জন্য স্পেশাল টগলস */}
              {selectedReport?.filters.includes("careToggles") && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 2 }}>Content Sections to Include</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "#F8FAFC" }}>
                    <Grid container spacing={1}>
                      {["Care Plans", "Daily Actions", "Observations", "Infections", "Incidents", "Bodymaps"].map(opt => (
                        <Grid item xs={12} sm={6} key={opt}>
                          <FormControlLabel control={<Switch defaultChecked size="small" />} label={<Typography variant="body2">{opt}</Typography>} />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Button 
                variant="contained" 
                fullWidth 
                size="large" 
                startIcon={<FileDownload />}
                sx={{ py: 2, borderRadius: "12px", fontWeight: "900", bgcolor: "#2563EB" }}
              >
                Run and Generate Report
              </Button>
            </Box>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
};

export default EasyReports;